import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { injectIntl, intlShape, FormattedMessage, defineMessages } from 'react-intl';
import Link from '../Link';
import FooterEmailCapture from './FooterEmailCapture';
import FooterLinkColumn from './FooterLinkColumn';

const messages = defineMessages({
  copyright: {
    id: 'footer.copyright',
    defaultMessage:
      'Copyright ©{year}. Fender Musical Instruments Corporation. All Rights Reserved.',
  },
  privacy: {
    id: 'footer.privacy',
    defaultMessage: 'Privacy Policy',
  },
  terms: {
    id: 'footer.terms',
    defaultMessage: 'Terms & Conditions',
  },
  supply: {
    id: 'footer.supply',
    defaultMessage: 'Supply Chain Transparency',
  },
  company: {
    id: 'footer.company',
    defaultMessage: 'Company',
  },
  customerService: {
    id: 'footer.customerService',
    defaultMessage: 'Customer Service',
  },
  moreFromFender: {
    id: 'footer.moreFromFender',
    defaultMessage: 'More from Fender',
  },
});
const footerStyle = 'futura-heavy w-100 bg-near-white flex flex-column items-center pv5 ph3';
const socialStyle = 'link mh3 hover-black dark-gray f1 dib';
const social = [
  { link: 'https://www.facebook.com/Fender/', icon: 'facebook' },
  { link: 'https://twitter.com/fender', icon: 'twitter' },
  { link: 'https://www.snapchat.com/add/fender', icon: 'snapchat' },
  { link: 'https://www.instagram.com/fender', icon: 'instagram' },
  { link: 'https://www.youtube.com/user/fendermusical', icon: 'youtube' },
  { link: 'https://plus.google.com/+fender', icon: 'google-plus' },
  { link: 'http://officialfender.tumblr.com/', icon: 'tumblr' },
  { link: 'https://www.pinterest.com/fender/', icon: 'pinterest' },
];

class Footer extends PureComponent {
  render() {
    const { footerLeft, footerCenter, footerRight, userEmail } = this.props;
    const { formatMessage } = this.props.intl;
    const trackEvent = {
      eventType: 'Click Footer Navigation',
      properties: {
        navigationFooter: '',
      },
    };
    const fullYear = new Date().getFullYear();
    return (
      <footer className={`footer ${footerStyle}`}>
        <div className="tc" style={{ maxWidth: '35rem' }}>
          <div className="tc mb3">
            {social.map(item => (
              <Link
                className={socialStyle}
                key={item.link}
                to={item.link}
                trackEvent={{ ...trackEvent, properties: { navigationFooter: item.icon } }}
                dataId={`footer-social-${item.icon}`}
              >
                <span className={`icon-${item.icon}`} />
              </Link>
            ))}
          </div>

          {this.props.basic ? null : (
            <div>
              <FooterEmailCapture
                requestInProgress={this.props.requestInProgress}
                createLead={this.props.createLead}
                leadsSuccessStatus={this.props.leadsSuccessStatus}
                leadsErrorStatus={this.props.leadsErrorStatus}
              />
              <div className="flex flex-wrap justify-between tl" data-id="footer-link-columns">
                <FooterLinkColumn
                  columnLinks={footerLeft}
                  columnTitle={formatMessage(messages.company)}
                  userEmail={userEmail}
                />
                <FooterLinkColumn
                  columnLinks={footerCenter}
                  columnTitle={formatMessage(messages.customerService)}
                  userEmail={userEmail}
                />
                <FooterLinkColumn
                  columnLinks={footerRight}
                  columnTitle={formatMessage(messages.moreFromFender)}
                  userEmail={userEmail}
                />
              </div>
            </div>
          )}

          <Link
            to="/"
            className="black inline-flex flex-auto items-center mb4"
            trackEvent={{ ...trackEvent, properties: { navigationFooter: 'Home' } }}
            dataId="footer-logo"
          >
            <span className="icon-logo" style={{ fontSize: '5rem' }} />
          </Link>
          <div className="flex flex-wrap justify-between source-sans tl lh-title">
            <p className="ma0-ns mb3 w-100 w-60-ns silver f7">
              <FormattedMessage
                {...messages.copyright}
                values={{
                  year: fullYear,
                }}
              />
            </p>
            <ul className="list ma0 pa0" data-id="footer-legal-links">
              <li>
                <Link
                  to="/pages/privacy"
                  trackEvent={{ ...trackEvent, properties: { navigationFooter: 'Privacy' } }}
                >
                  <FormattedMessage {...messages.privacy} />
                </Link>
              </li>
              <li>
                <Link
                  to="/pages/terms-of-use"
                  trackEvent={{
                    ...trackEvent,
                    properties: { navigationFooter: 'Terms & Conditions' },
                  }}
                >
                  <FormattedMessage {...messages.terms} />
                </Link>
              </li>
              <li>
                <Link
                  to={`${process.env.REACT_APP_CLIENT_FENDER_COM}/pages/supply-chain-transparency`}
                  trackEvent={{
                    ...trackEvent,
                    properties: { navigationFooter: 'Supply Chain Transparency' },
                  }}
                >
                  <FormattedMessage {...messages.supply} />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </footer>
    );
  }
}

Footer.propTypes = {
  basic: PropTypes.bool,
  footerLeft: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      attributes: PropTypes.shape({
        order: PropTypes.number.isRequired,
        group: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        'is-external-url': PropTypes.bool.isRequired,
        url: PropTypes.string.isRequired,
      }),
    }),
  ),
  footerCenter: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      attributes: PropTypes.shape({
        order: PropTypes.number.isRequired,
        group: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        'is-external-url': PropTypes.bool.isRequired,
        url: PropTypes.string.isRequired,
      }),
    }),
  ),
  footerRight: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      attributes: PropTypes.shape({
        order: PropTypes.number.isRequired,
        group: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        'is-external-url': PropTypes.bool.isRequired,
        url: PropTypes.string.isRequired,
      }),
    }),
  ),
  intl: intlShape.isRequired,
  requestInProgress: PropTypes.bool.isRequired,
  createLead: PropTypes.func.isRequired,
  leadsSuccessStatus: PropTypes.bool.isRequired,
  leadsErrorStatus: PropTypes.string,
  userEmail: PropTypes.string,
};

Footer.defaultProps = {
  footerLeft: [],
  footerCenter: [],
  footerRight: [],
  basic: false,
  leadsErrorStatus: null,
  userEmail: null,
};

export default injectIntl(Footer);
